"use client";

import { useState } from "react";
import { Button, ExpandLessIcon, ExpandMoreIcon } from "@aidnas/design-system";
import { Tag } from "@aidnas/design-system-bandaid";
import { vercelStegaClean } from "@vercel/stega";
import { useParams } from "next/navigation";

import styles from "./Styles.module.scss";

import Link from "@/components/Link";
import BEMHelper from "@/lib/bem";
import { cleanSections } from "@/lib/sections";
import { SiteQueryResult } from "@/types/sanity";

type HeaderProps = {
  sections?: NonNullable<SiteQueryResult>["sections"];
};

const bem = BEMHelper(styles);

function Label({ label }: { label: string | null }) {
  const cleanLabel = vercelStegaClean(label);

  if (!cleanLabel) {
    return null;
  }

  let labelColor: string;

  switch (cleanLabel) {
    case "🧪 Lab":
      labelColor = "green";
      break;
    case "updated":
      labelColor = "yellow";
      break;
    default:
      labelColor = "blue";
  }

  return (
    <Tag bordered color={labelColor} {...bem("tag")}>
      {cleanLabel.toUpperCase()}
    </Tag>
  );
}

export default function Sidebar({ sections }: HeaderProps) {
  const cleanedSections = cleanSections(sections);
  const [collapsed, setCollapsed] = useState(true);
  const { section, slug } = useParams<{ site: string; section?: string; slug?: string }>();
  const activeSection = cleanedSections?.find((s) => s.slug?.current === section);
  const activeDocPage =
    activeSection?.sidebar
      ?.flatMap((s) => s.docPages || [])
      .find((docPage) => docPage.slug?.current === slug) || null;

  return (
    <nav {...bem("")}>
      <Button
        density="spacious"
        onClick={() => setCollapsed(!collapsed)}
        variant="outline"
        {...bem("toggle")}
        iconEnd={collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
      >
        <span>
          {activeSection?.title}
          {activeDocPage ? ` > ${activeDocPage.title}` : null}
        </span>
      </Button>
      <ul {...bem("sections", { collapsed })}>
        {activeSection?.sidebar?.map((sidebarSection) => {
          if (sidebarSection.hidden) return null;

          return (
            <li key={sidebarSection._key}>
              <div {...bem("section-title")}>{sidebarSection.title}</div>

              <ul {...bem("section-pages")}>
                {sidebarSection?.docPages?.map((docPage) => (
                  <li key={docPage._id}>
                    <Link
                      href={`/${activeSection?.slug?.current}/${docPage.slug?.current}`}
                      {...bem("page", { active: docPage.slug?.current === slug })}
                    >
                      <span {...bem("title")}>{docPage.title}</span> <Label label={docPage.label} />
                    </Link>
                  </li>
                ))}
              </ul>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
